import { useState, useEffect } from 'react';
import { useAdminVendors, useAdminVendor } from './useAdminVendors';
import { useServiceCategories } from '../services/serviceCategoryService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook for managing vendor success story form data.
 * Handles vendor selection with auto-population, service categories, and current admin.
 */
export const useVendorSuccessStoryForm = ({ isOpen, isEdit, story }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    vendor_name: '',
    vendor_id: '',
    category: '',
    image: '',
    quote: '',
    author: '',
    role: '',
    stats: { bookings: '', rating: '', revenue: '' },
    featured: false,
    sort_order: 0,
    is_active: true,
  });
  const [selectedVendorId, setSelectedVendorId] = useState('');

  // Fetch active/verified vendors (for both create and edit - to link vendor)
  const { data: vendorsResponse } = useAdminVendors(
    { per_page: 100, verified: true },
    { enabled: isOpen }
  );

  // Fetch vendor details when selected
  const { data: vendorDetails } = useAdminVendor(selectedVendorId, {
    enabled: !!selectedVendorId,
  });

  // Fetch service categories for dropdown
  const { data: categoriesResponse } = useServiceCategories({});

  // Current admin for author/role
  const adminName = user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || '';
  const adminRole = user?.user_type === 'admin' ? 'Administrator' : user?.user_type || '';

  // Auto-populate when vendor is selected (works for both create and edit)
  useEffect(() => {
    if (!vendorDetails?.data) return;

    const vendor = vendorDetails.data;
    const vp = vendor.vendor_profile || vendor.vendorProfile || {};

    // Category from first vendor category
    const firstVendorCat = vp.vendor_categories?.[0] || vp.vendorCategories?.[0];
    const categoryName = firstVendorCat?.service_category?.category_name || firstVendorCat?.serviceCategory?.category_name || '';

    // Format revenue
    const totalRev = vp.total_revenue ?? 0;
    const revenueStr = totalRev > 0 ? `$${Number(totalRev).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '';

    setFormData((prev) => ({
      ...prev,
      vendor_name: vp.business_name || vendor.name || '',
      vendor_id: vp.id || '',
      category: categoryName,
      image: vp.image_url || vendor.profile_image_url || '',
      stats: {
        bookings: vp.bookings_count ?? '',
        rating: vp.average_rating ?? '',
        revenue: revenueStr,
      },
    }));
  }, [vendorDetails]);

  // Initialize when editing or reset when creating
  useEffect(() => {
    if (isEdit && story) {
      setFormData({
        vendor_name: story.vendor_name || '',
        vendor_id: story.vendor_id || '',
        category: story.category || '',
        image: story.image || '',
        quote: story.quote || '',
        author: story.author || adminName,
        role: story.role || adminRole,
        stats: story.stats || { bookings: '', rating: '', revenue: '' },
        featured: story.featured ?? false,
        sort_order: story.sort_order ?? 0,
        is_active: story.is_active ?? true,
      });
      // Pre-select vendor when editing if story has vendor_id
      if (story.vendor_id) {
        const vendors = vendorsResponse?.data?.data || vendorsResponse?.data || [];
        const matched = Array.isArray(vendors)
          ? vendors.find((v) => (v.vendor_profile || v.vendorProfile)?.id === story.vendor_id)
          : null;
        setSelectedVendorId(matched?.id || '');
      } else {
        setSelectedVendorId('');
      }
    } else if (isOpen && !isEdit) {
      setFormData({
        vendor_name: '',
        vendor_id: '',
        category: '',
        image: '',
        quote: '',
        author: adminName,
        role: adminRole,
        stats: { bookings: '', rating: '', revenue: '' },
        featured: false,
        sort_order: 0,
        is_active: true,
      });
      setSelectedVendorId('');
    }
  }, [isEdit, story, isOpen, adminName, adminRole, vendorsResponse]);

  // Vendor options for SearchSelect
  const vendors = vendorsResponse?.data?.data || vendorsResponse?.data || [];
  const vendorOptions = Array.isArray(vendors)
    ? vendors
        .filter((v) => v && (v.vendor_profile || v.vendorProfile))
        .map((vendor) => {
          const vp = vendor.vendor_profile || vendor.vendorProfile;
          const name = vp?.business_name || vendor.name || 'Vendor';
          const city = vp?.city || '';
          const state = vp?.state || '';
          const loc = [city, state].filter(Boolean).join(', ');
          return {
            value: vendor.id,
            label: loc ? `${name} - ${loc}` : name,
          };
        })
    : [];

  // Category options from DB (include form's current category if not in list, e.g. from vendor or legacy)
  const categories = categoriesResponse?.data?.data || categoriesResponse?.data || [];
  const baseCategoryOptions = Array.isArray(categories)
    ? categories
        .filter((c) => c && (c.is_active !== false))
        .map((c) => ({
          value: c.category_name || c.id,
          label: c.category_name || c.id,
        }))
    : [];
  const categoryOptions =
    formData.category && !baseCategoryOptions.some((o) => o.value === formData.category)
      ? [{ value: formData.category, label: formData.category }, ...baseCategoryOptions]
      : baseCategoryOptions;

  return {
    formData,
    setFormData,
    selectedVendorId,
    setSelectedVendorId,
    vendorOptions,
    categoryOptions,
    isLoadingVendors: false,
    isLoadingCategories: !categoriesResponse,
  };
};
